import { Inject, Injectable, OnDestroy, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Injectable({ providedIn: 'root' })
export class NotificacaoBrowserService implements OnDestroy {

  private tituloOriginal = '';
  private piscarInterval: any = null;
  private ultimoSomTimestamp = 0;
  private readonly THROTTLE_SOM_MS = 3000;
  private readonly isBrowser: boolean;

  // Preferencia do usuario: som habilitado/desabilitado (persistida no localStorage)
  private readonly STORAGE_KEY_SOM = 'notificacao-pedido-som-habilitado';

  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    this.isBrowser = isPlatformBrowser(platformId);

    if (this.isBrowser) {
      this.tituloOriginal = document.title;
      // Restaurar titulo ao focar na aba
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }
  }

  ngOnDestroy(): void {
    this.pararPiscarTitulo();
    if (this.isBrowser) {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
  }

  /** Retorna se a aba esta visivel */
  get abaVisivel(): boolean {
    if (!this.isBrowser) return false;
    return document.visibilityState === 'visible';
  }

  /** Solicita permissao para Browser Notifications (chamar uma vez) */
  solicitarPermissaoNotificacao(): void {
    if (!this.isBrowser) return;
    if (!('Notification' in window)) return;
    if (Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {
        // Silencioso: usuario pode negar
      });
    }
  }

  /** Inicia o piscar do titulo da aba */
  piscarTitulo(mensagem: string): void {
    if (!this.isBrowser) return;
    if (this.piscarInterval) return; // Ja esta piscando

    this.tituloOriginal = document.title;
    let mostrando = false;

    this.piscarInterval = setInterval(() => {
      document.title = mostrando ? this.tituloOriginal : mensagem;
      mostrando = !mostrando;
    }, 1000);
  }

  /** Para de piscar o titulo e restaura o original */
  pararPiscarTitulo(): void {
    if (this.piscarInterval) {
      clearInterval(this.piscarInterval);
      this.piscarInterval = null;
      document.title = this.tituloOriginal;
    }
  }

  /** Toca som de notificacao (com throttle para nao empilhar) */
  tocarSom(): void {
    if (!this.isBrowser) return;
    if (!this.somHabilitado) return;
    const agora = Date.now();
    if (agora - this.ultimoSomTimestamp < this.THROTTLE_SOM_MS) return;

    this.ultimoSomTimestamp = agora;
    const audio = new Audio('assets/sounds/notificacao-pedido.mp3');
    audio.volume = 0.5;
    audio.play().catch(() => {
      // Silencioso: autoplay policy pode bloquear
    });
  }

  /** Preferencia do usuario: tocar som ao receber novo pedido. Padrao: true. */
  get somHabilitado(): boolean {
    if (!this.isBrowser) return false;
    const valor = localStorage.getItem(this.STORAGE_KEY_SOM);
    return valor === null ? true : valor === 'true';
  }

  set somHabilitado(habilitado: boolean) {
    if (!this.isBrowser) return;
    localStorage.setItem(this.STORAGE_KEY_SOM, habilitado ? 'true' : 'false');
  }

  /** Envia Browser Notification */
  enviarNotificacao(titulo: string, corpo: string): void {
    if (!this.isBrowser) return;
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;

    try {
      new Notification(titulo, {
        body: corpo,
        icon: 'assets/icons/icon-192x192.png',
        tag: 'notificacao-pedido', // Evita duplicatas
      });
    } catch {
      // Silencioso: alguns browsers nao suportam Notification constructor
    }
  }

  /**
   * Notificacao completa para aba inativa: piscar titulo + som.
   *
   * ATENCAO: NAO chama enviarNotificacao() aqui.
   * Quando o app esta em background, o FCM roteia a mensagem para o Service Worker
   * (firebase-messaging-sw.js → onBackgroundMessage → showNotification).
   * Se chamassemos new Notification() aqui tambem, a notificacao apareceria duplicada:
   * uma via SW (showNotification) e outra via Notification constructor.
   * O SW tem responsabilidade exclusiva sobre notificacoes push de background.
   */
  notificarAbaInativa(titulo: string, corpo: string, tituloAba: string): void {
    this.piscarTitulo(tituloAba);
    this.tocarSom();
  }

  // Handler de visibilitychange
  private onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      this.pararPiscarTitulo();
    }
  };
}
